'use client'

import Image from 'next/image'
import React, { useRef } from 'react'
import Carousel from 'react-multi-carousel'

interface ISliderProps {
    children: React.ReactNode
    /** Itens por vez no desktop (a partir de 1024 px): 4 nas vitrines e no blog, 3 no carrinho. */
    desktopItems?: number
    /** Classe do fundo das setas no hover (padrão `hover:bg-secondary-alpha`). */
    buttonClass?: string
    /** @deprecated Sem efeito (o carrossel nunca leu); o SectionBlog ainda manda. */
    maxProducts?: number
    /** @deprecated Sem efeito (o carrossel nunca leu); o SectionBlog ainda manda. */
    partialVisibilityGutter?: number
}

const ArrowButton: React.FC<{ label: string; onClick: () => void; hoverClass: string; flipped?: boolean }> = ({
    label,
    onClick,
    hoverClass,
    flipped = false,
}) => (
    <button
        type="button"
        className={`flex bg-transparent rounded-full relative cursor-pointer items-center justify-center h-[45px] w-[45px] transition-all duration-200 ${hoverClass}`}
        aria-label={label}
        onClick={onClick}
    >
        <Image
            src="/assets/icons/right-arrow.svg"
            alt={label}
            width={16}
            height={10}
            className={flipped ? 'transform select-none -scale-x-100' : 'select-none hover:color-white'}
        />
    </button>
)

/** Setas embaixo do carrossel (componente fixo: criado na renderização, perderia o estado a cada render). */
const SliderArrows: React.FC<{ carouselRef: React.RefObject<Carousel | null>; hoverClass: string }> = ({
    carouselRef,
    hoverClass,
}) => (
    <div className="gap-8 mt-6 lg:mb-4 md:mb-24">
        <div className="flex flex-wrap justify-center">
            <ArrowButton
                label="Slide anterior"
                onClick={() => carouselRef.current?.previous(0)}
                hoverClass={hoverClass}
            />
            <ArrowButton
                label="Próximo slide"
                onClick={() => carouselRef.current?.next(0)}
                hoverClass={hoverClass}
                flipped
            />
        </div>
    </div>
)

/**
 * Carrossel de cards da loja (vitrines da home e da PDP, recomendações, carrinho e blog), com as setas embaixo. Um só
 * para a loja: o antigo Slider2 era uma cópia com 3 itens no desktop (`desktopItems={3}`).
 */
const Slider: React.FC<ISliderProps> = ({ children, buttonClass = '', desktopItems = 4 }) => {
    const ref = useRef<Carousel>(null)
    const hoverClass = buttonClass.length > 0 ? buttonClass : 'hover:bg-secondary-alpha'

    return (
        <Carousel
            ref={ref}
            customButtonGroup={<SliderArrows carouselRef={ref} hoverClass={hoverClass} />}
            arrows={false}
            additionalTransfrom={0}
            autoPlaySpeed={2000}
            centerMode={false}
            className=""
            containerClass=""
            dotListClass=""
            draggable
            focusOnSelect={false}
            infinite={true}
            itemClass=""
            keyBoardControl
            minimumTouchDrag={80}
            pauseOnHover
            renderArrowsWhenDisabled={true}
            renderButtonGroupOutside
            renderDotsOutside={false}
            responsive={{
                desktop: {
                    breakpoint: {
                        max: 3000,
                        min: 1024,
                    },
                    items: desktopItems,
                    partialVisibilityGutter: 40,
                },
                mobile: {
                    breakpoint: {
                        max: 464,
                        min: 0,
                    },
                    items: 1,
                    partialVisibilityGutter: 30,
                },
                tablet: {
                    breakpoint: {
                        max: 1024,
                        min: 464,
                    },
                    items: 2,
                    partialVisibilityGutter: 30,
                },
            }}
            rewind={false}
            rewindWithAnimation={false}
            rtl={false}
            shouldResetAutoplay
            showDots={false}
            sliderClass=""
            slidesToSlide={1}
            swipeable
        >
            {children}
        </Carousel>
    )
}

export default Slider
